import { useState } from "react";
import { toast } from "sonner";
import { Plane, Check } from "lucide-react";
import { format } from "date-fns";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import TravellersPicker from "@/components/TravellersPicker";
import DateRangePicker from "@/components/DateRangePicker";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { usePage } from "@/context/PagesContext";

const COUNTRY_GROUPS = [
  {
    label: "Southeast Asia",
    items: ["Vietnam","Cambodia","Laos","Thailand","Malaysia & Borneo","Singapore","Indonesia (incl. Bali)","Philippines"],
  },
  {
    label: "East Asia",
    items: ["Japan","China","Hong Kong","Taiwan","South Korea","Tibet"],
  },
  {
    label: "Beyond Asia",
    items: ["Sri Lanka","The Maldives","The UAE"],
  },
];

const CABIN_OPTIONS = ["Economy", "Premium Economy", "Business"];

const initialData = {
  destinations: [],
  other_selected: false,
  other_destination: "",
  dates: { from: undefined, to: undefined },
  travellers: { adults: 2, children: 0, infants: 0 },
  flights_required: false,
  home_airport: "",
  secondary_airport: "",
  cabin_class: "",
  budget: "",
  name: "",
  email: "",
  phone: "",
  notes: "",
};

export default function Contact() {
  const { settings } = useSiteSettings();
  const s = settings || {};
  const { copy, seo } = usePage("contact");
  const [step, setStep] = useState(1);
  const [data, setData] = useState(initialData);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const toggleCountry = (c) => {
    if (c === "Other") {
      setData((s) => ({ ...s, other_selected: !s.other_selected, other_destination: !s.other_selected ? s.other_destination : "" }));
      return;
    }
    setData((s) => ({
      ...s,
      destinations: s.destinations.includes(c) ? s.destinations.filter(x => x !== c) : [...s.destinations, c]
    }));
  };

  const step1Valid = (data.destinations.length > 0 || (data.other_selected && data.other_destination.trim().length > 0));
  const step2Valid = !data.flights_required || (data.home_airport.trim().length > 0 && data.cabin_class);
  const step3Valid = data.name.trim() && /\S+@\S+\.\S+/.test(data.email) && data.phone.trim() && data.notes.trim();

  const travellersSummary = () => {
    const t = data.travellers;
    const parts = [`${t.adults} adult${t.adults === 1 ? "" : "s"}`];
    if (t.children > 0) parts.push(`${t.children} child${t.children === 1 ? "" : "ren"}`);
    if (t.infants > 0) parts.push(`${t.infants} infant${t.infants === 1 ? "" : "s"}`);
    return parts.join(", ");
  };

  const datesString = () => {
    const { from, to } = data.dates;
    if (from && to) return `${format(from, "d MMM yyyy")} to ${format(to, "d MMM yyyy")}`;
    if (from) return format(from, "d MMM yyyy");
    return null;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        name: data.name.trim(),
        email: data.email.trim(),
        phone: data.phone.trim(),
        destinations: data.destinations,
        other_destination: data.other_selected ? data.other_destination.trim() : null,
        travel_dates: datesString(),
        date_start: data.dates.from ? data.dates.from.toISOString().slice(0,10) : null,
        date_end: data.dates.to ? data.dates.to.toISOString().slice(0,10) : null,
        travellers: travellersSummary(),
        adults: data.travellers.adults,
        children: data.travellers.children,
        infants: data.travellers.infants,
        budget: data.budget || null,
        flights_required: data.flights_required,
        home_airport: data.flights_required ? data.home_airport.trim() : null,
        secondary_airport: data.flights_required ? (data.secondary_airport.trim() || null) : null,
        cabin_class: data.flights_required ? data.cabin_class : null,
        notes: data.notes.trim(),
      };
      await api.post("/enquiries", payload);
      setSubmitted(true);
      toast.success("Your enquiry has reached our concierge.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <Seo title={seo.title} description={seo.description} og_title={seo.og_title} og_description={seo.og_description} />
      {/* Hero strip */}
      <section className="pt-40 pb-16 bg-ivory">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 text-center">
          <p className="eyebrow text-copper">{copy("hero_eyebrow", "Begin")}</p>
          <h1 className="mt-8 font-serif text-5xl md:text-7xl text-jade font-light leading-[1.02]">
            {copy("hero_h1_part1", "Start your")} <span className="italic">{copy("hero_h1_part2_italic", "custom journey")}</span>.
          </h1>
          <p className="mt-8 text-ink/75 text-[16px] md:text-[17px] font-light leading-[1.85] max-w-2xl mx-auto">
            {copy("hero_intro", "A short conversation with a member of our team and your journey begins to take shape. Most enquiries receive a personal reply within one working day.")}
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {submitted ? (
            <div data-testid="enquiry-success" className="bg-sand p-12 md:p-20 text-center">
              <div className="mx-auto w-14 h-14 rounded-full border border-jade flex items-center justify-center">
                <Check strokeWidth={1.2} className="w-6 h-6 text-jade" />
              </div>
              <p className="eyebrow text-copper mt-8">{copy("success_eyebrow", "Received with thanks")}</p>
              <h2 className="mt-5 font-serif text-3xl md:text-5xl text-jade font-light leading-tight">{copy("success_h2", "Your enquiry is with us.")}</h2>
              <p className="mt-6 text-ink/75 text-[16px] font-light leading-[1.85] max-w-lg mx-auto">
                {copy("success_body", "A member of our team will write to you personally, usually within one working day.")} In the meantime, do explore our <a href="/journeys" className="text-jade underline-offset-4 hover:underline">sample journeys</a> or our <a href="/journal" className="text-jade underline-offset-4 hover:underline">journal</a>.
              </p>
            </div>
          ) : (
            <div className="bg-sand p-8 md:p-14">
              {/* Step progress */}
              <div className="flex items-center justify-between mb-12">
                {[1,2,3].map((n) => (
                  <div key={n} className="flex items-center gap-4 flex-1">
                    <div className={`flex items-center gap-3 ${step >= n ? "" : "opacity-40"}`}>
                      <span className={`font-serif text-2xl italic ${step >= n ? "text-copper" : "text-ink/40"}`}>{String(n).padStart(2,"0")}</span>
                      <span className="hidden md:inline text-[11px] tracking-[0.18em] uppercase text-ink/70">
                        {n === 1 ? "Where & when" : n === 2 ? "How you travel" : "About you"}
                      </span>
                    </div>
                    {n < 3 && <span className={`flex-1 h-px ${step > n ? "bg-copper" : "bg-mist/60"}`} />}
                  </div>
                ))}
              </div>

              {step === 1 && (
                <div className="space-y-10 fade-up">
                  <div>
                    <p className="eyebrow">Step One</p>
                    <h2 className="mt-3 font-serif text-3xl md:text-4xl text-jade font-light leading-tight">Where are you dreaming of?</h2>
                    <p className="mt-3 text-ink/70 text-[14px] font-light">Select one or several. We design multi-country journeys all the time.</p>
                  </div>

                  {/* Country groups */}
                  <div className="space-y-7">
                    {COUNTRY_GROUPS.map((group) => (
                      <div key={group.label}>
                        <p className="eyebrow text-ink/55 text-[10px]">{group.label}</p>
                        <div className="flex flex-wrap gap-2 pt-3">
                          {group.items.map((c) => (
                            <button
                              key={c}
                              data-testid={`enquiry-dest-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                              type="button"
                              onClick={() => toggleCountry(c)}
                              className={`text-[12px] tracking-[0.12em] uppercase px-4 py-2 border transition-colors ${
                                data.destinations.includes(c) ? "bg-jade text-ivory border-jade" : "border-mist text-ink/75 hover:border-jade"
                              }`}
                            >{c}</button>
                          ))}
                        </div>
                      </div>
                    ))}

                    <div>
                      <p className="eyebrow text-ink/55 text-[10px]">Not listed</p>
                      <div className="flex flex-wrap gap-2 pt-3">
                        <button
                          type="button"
                          data-testid="enquiry-dest-other"
                          onClick={() => toggleCountry("Other")}
                          className={`text-[12px] tracking-[0.12em] uppercase px-4 py-2 border italic transition-colors ${
                            data.other_selected ? "bg-copper text-ivory border-copper" : "border-mist text-ink/75 hover:border-copper"
                          }`}
                        >Other / Not listed</button>
                      </div>
                      {data.other_selected && (
                        <div className="mt-5 fade-up">
                          <label className="eyebrow text-ink/55">Please tell us where <span className="text-copper">*</span></label>
                          <input
                            data-testid="enquiry-other-country"
                            value={data.other_destination}
                            onChange={(e) => set("other_destination", e.target.value)}
                            placeholder="e.g. Mongolia, Nepal, Oman"
                            className="input-line mt-3"
                            required
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8 pt-2">
                    <div>
                      <label className="eyebrow text-ink/55">Travel dates</label>
                      <div className="mt-3">
                        <DateRangePicker value={data.dates} onChange={(v) => set("dates", v)} />
                      </div>
                    </div>
                    <div>
                      <label className="eyebrow text-ink/55">Travellers</label>
                      <div className="mt-3">
                        <TravellersPicker value={data.travellers} onChange={(v) => set("travellers", v)} />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-6">
                    <button data-testid="enquiry-next-1" disabled={!step1Valid} onClick={() => setStep(2)} className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed">Continue</button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-10 fade-up">
                  <div>
                    <p className="eyebrow">Step Two</p>
                    <h2 className="mt-3 font-serif text-3xl md:text-4xl text-jade font-light leading-tight">How you'd like to travel.</h2>
                    <p className="mt-3 text-ink/70 text-[14px] font-light">A few preferences so we can shape the right proposal.</p>
                  </div>

                  <div>
                    <p className="eyebrow text-ink/55">Would you like flights from your home airport included in the proposal?</p>
                    <div className="mt-4 flex gap-3 flex-wrap">
                      {[{v:true,l:"Yes, please include flights"}, {v:false,l:"No, just the journey on the ground"}].map((opt) => (
                        <button
                          key={String(opt.v)}
                          type="button"
                          data-testid={`enquiry-flights-${opt.v ? "yes" : "no"}`}
                          onClick={() => set("flights_required", opt.v)}
                          className={`flex items-center gap-3 px-5 py-3 border text-[12px] tracking-[0.12em] uppercase transition-colors ${
                            data.flights_required === opt.v ? "bg-jade text-ivory border-jade" : "border-mist text-ink/75 hover:border-jade"
                          }`}
                        >
                          <Plane strokeWidth={1} className="w-4 h-4" /> {opt.l}
                        </button>
                      ))}
                    </div>
                  </div>

                  {data.flights_required && (
                    <div className="fade-up space-y-8 border-l-2 border-copper/60 pl-6 md:pl-10">
                      <div className="grid md:grid-cols-2 gap-8">
                        <div>
                          <label className="eyebrow text-ink/55">Home airport <span className="text-copper">*</span></label>
                          <input
                            data-testid="enquiry-home-airport"
                            value={data.home_airport}
                            onChange={(e) => set("home_airport", e.target.value)}
                            placeholder="e.g. London Heathrow (LHR)"
                            className="input-line mt-3"
                          />
                        </div>
                        <div>
                          <label className="eyebrow text-ink/55">Secondary airport <span className="text-ink/40 normal-case tracking-normal italic ml-1">(optional)</span></label>
                          <input
                            data-testid="enquiry-secondary-airport"
                            value={data.secondary_airport}
                            onChange={(e) => set("secondary_airport", e.target.value)}
                            placeholder="e.g. London Gatwick (LGW)"
                            className="input-line mt-3"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="eyebrow text-ink/55">Preferred cabin class <span className="text-copper">*</span></label>
                        <div className="mt-4 flex flex-wrap gap-3">
                          {CABIN_OPTIONS.map((c) => (
                            <button
                              key={c}
                              type="button"
                              data-testid={`enquiry-cabin-${c.toLowerCase().replace(/\s/g, "-")}`}
                              onClick={() => set("cabin_class", c)}
                              className={`text-[12px] tracking-[0.12em] uppercase px-5 py-2.5 border transition-colors ${
                                data.cabin_class === c ? "bg-jade text-ivory border-jade" : "border-mist text-ink/75 hover:border-jade"
                              }`}
                            >{c}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="pt-2">
                    <label className="eyebrow text-ink/55">Approximate budget per person <span className="text-ink/40 normal-case tracking-normal italic ml-1">(optional)</span></label>
                    <input data-testid="enquiry-budget" placeholder="e.g. £8,000 to £12,000 per person" className="input-line mt-3" value={data.budget} onChange={(e) => set("budget", e.target.value)} />
                    <p className="mt-2 text-[12px] text-ink/50 italic">A guide only. We will always quote within your considered range.</p>
                  </div>

                  <div className="flex justify-between pt-6 gap-4">
                    <button onClick={() => setStep(1)} className="btn-secondary">Back</button>
                    <button data-testid="enquiry-next-2" disabled={!step2Valid} onClick={() => setStep(3)} className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed">Continue</button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-10 fade-up">
                  <div>
                    <p className="eyebrow">Step Three</p>
                    <h2 className="mt-3 font-serif text-3xl md:text-4xl text-jade font-light leading-tight">A little about you.</h2>
                    <p className="mt-3 text-ink/70 text-[14px] font-light">We keep your details in strict confidence, used only to design your proposal.</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <label className="eyebrow text-ink/55">Full name <span className="text-copper">*</span></label>
                      <input data-testid="enquiry-name" value={data.name} onChange={(e) => set("name", e.target.value)} className="input-line mt-3" required />
                    </div>
                    <div>
                      <label className="eyebrow text-ink/55">Email <span className="text-copper">*</span></label>
                      <input data-testid="enquiry-email" type="email" value={data.email} onChange={(e) => set("email", e.target.value)} className="input-line mt-3" required />
                    </div>
                  </div>

                  <div>
                    <label className="eyebrow text-ink/55">Phone <span className="text-copper">*</span></label>
                    <input data-testid="enquiry-phone" type="tel" placeholder="Including international dialling code" value={data.phone} onChange={(e) => set("phone", e.target.value)} className="input-line mt-3" required />
                  </div>

                  <div>
                    <label className="eyebrow text-ink/55">Tell us about the journey you have in mind <span className="text-copper">*</span></label>
                    <textarea
                      data-testid="enquiry-notes"
                      value={data.notes}
                      onChange={(e) => set("notes", e.target.value)}
                      placeholder="Interests, pace, special occasions, who you're travelling with, hotels you've loved before…"
                      className="input-line min-h-[180px] resize-y mt-3"
                      required
                    />
                    <p className="mt-3 text-[13px] text-ink/55 italic leading-relaxed">
                      Share as much or as little as you like. The more you tell us about how you like to travel, the more thoughtfully we can shape something just for you.
                    </p>
                  </div>

                  <div className="flex justify-between pt-6 gap-4">
                    <button onClick={() => setStep(2)} className="btn-secondary">Back</button>
                    <button data-testid="enquiry-submit" disabled={submitting || !step3Valid} onClick={submit} className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed">
                      {submitting ? "Sending…" : "Send to Concierge"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Studio info strip */}
      <section className="py-20 md:py-28 bg-ivory border-t border-mist/40">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-12">
          <div>
            <p className="eyebrow">Studio</p>
            <p className="mt-4 font-serif text-2xl text-jade" data-testid="contact-address-1">{s.address_line1 || "2 & 3 Fish Hill"}</p>
            <p className="text-ink/70 text-[14px] mt-1" data-testid="contact-address-2">
              {[s.address_line2, s.postcode].filter(Boolean).join(", ") || "Royston, Hertfordshire, SG8 9LD"}
            </p>
          </div>
          <div>
            <p className="eyebrow">Concierge</p>
            <p className="mt-4 font-serif text-2xl text-jade" data-testid="contact-phone">{s.phone || "(+44) 01763 463888"}</p>
            {s.hours_weekday && <p className="text-ink/70 text-[14px] mt-1">{s.hours_weekday}</p>}
            {s.hours_weekend && <p className="text-ink/70 text-[14px]">{s.hours_weekend}</p>}
          </div>
          <div>
            <p className="eyebrow">Email</p>
            <p className="mt-4 font-serif text-2xl text-jade" data-testid="contact-email">{s.email || "enquiries@abbotasia.co.uk"}</p>
            <p className="text-ink/70 text-[14px] mt-1">Most enquiries receive a personal reply within one working day</p>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
